import { useEffect, useState } from 'react';

/**
 * Whether the viewport is at or below {@code maxWidth}.
 *
 * Driven by `matchMedia` rather than a resize listener, so it fires only when
 * the answer actually changes rather than on every pixel of a drag, and it
 * reports correctly on the very first render instead of after an effect.
 */
export const useIsNarrowViewport = (maxWidth: number): boolean => {
  const query = `(max-width: ${maxWidth}px)`;

  const [isNarrow, setIsNarrow] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const media = window.matchMedia(query);
    const update = (event: MediaQueryListEvent) => setIsNarrow(event.matches);
    // re-sync on mount too: the query string can change between renders
    setIsNarrow(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return isNarrow;
};
